// src/utils/bmrApi.js
// API helpers for the "Vitals with Pathology" (service_id 34) workflow.
//
// Routes used (all exist in the Django urls):
//   POST /api/technician/dc-camp/bmr/upload/                  (BMRPdfUploadView)
//   GET  /api/technician/dc-camp/bmr/records/                 (BMRPdfRecordListView)
//   GET  /api/technician/dc-camp/bmr/records/<record_id>/     (BMRPdfRecordDetailView)
//   GET/POST/PATCH /api/technician/patients/<patient_id>/vitals/   (PatientVitalsView — CONFIRMED)
//
// PatientVitalsView facts (from the view code):
//   - <patient_id> is the INTEGER PatientData pk (not patient_unique_id).
//   - GET  -> { success, record: {...} | null }
//   - POST -> creates; 409 if the patient already has vitals.
//   - PATCH/PUT -> updates; 404 if no vitals exist yet.
//   - Errors: 400 { success:false, errors:{field:msg} }.
//   - Keys that are not in VITALS_FIELDS are silently ignored by the backend.
//
// Still assumed (not confirmed): upload multipart field name ("file") and the supported
// query params of the BMR records list view.

export const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://cbackend.xraidigital.com";

const AUTH_SCHEME = "Token"; // change to "Bearer" if the backend uses JWT
const UPLOAD_FILE_FIELD = "file"; // multipart field name expected by BMRPdfUploadView (confirm)

export const BMR_ENDPOINTS = {
  upload: "/api/technician/dc-camp/bmr/upload/",
  records: "/api/technician/dc-camp/bmr/records/",
  recordDetail: (id) => `/api/technician/dc-camp/bmr/records/${encodeURIComponent(id)}/`,
  // Uses patient_unique_id, not the integer database patient_id.
  patientVitals: (patientUniqueId) =>
    `/api/technician/patients/${encodeURIComponent(patientUniqueId)}/vitals/`,
};

export class ApiError extends Error {
  constructor(message, status = 0, data = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.isAuthError = status === 401 || status === 403;
    this.endpointMissing = status === 404 || status === 405;
  }
}

const getToken = () => {
  try {
    return localStorage.getItem("authToken");
  } catch {
    return null;
  }
};

const SESSION_MSG = "Your session has expired or you are not logged in. Please log in again.";

// Token is sent only if present (the dashboard's own calls work without one); the server decides.
const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `${AUTH_SCHEME} ${token}` } : {};
};

const extractMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  // Django view style: { success:false, errors: { field: "message" } }
  if (data.errors && typeof data.errors === "object") {
    const parts = Object.entries(data.errors).map(
      ([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : typeof v === "object" ? JSON.stringify(v) : v}`
    );
    if (parts.length) return parts.join("; ");
  }
  if (data.error) return typeof data.error === "string" ? data.error : JSON.stringify(data.error);
  if (data.detail) return data.detail;
  if (data.message) return data.message;
  if (typeof data === "object") {
    const parts = Object.entries(data)
      .filter(([k, v]) => k !== "success" && v !== null && v !== undefined)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : typeof v === "object" ? JSON.stringify(v) : v}`);
    if (parts.length) return parts.join("; ");
  }
  return fallback;
};

async function request(path, { method = "GET", body, query } = {}) {
  const headers = { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}), ...authHeaders() };
  const cleaned = query
    ? Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== ""))
    : null;
  const qs = cleaned && Object.keys(cleaned).length ? `?${new URLSearchParams(cleaned).toString()}` : "";

  let res;
  try {
    res = await fetch(`${API_BASE}${path}${qs}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  } catch {
    throw new ApiError("Network error. Check your connection and try again.");
  }

  let data = null;
  let nonJson = false;
  try {
    data = await res.json();
  } catch {
    nonJson = true;
  }

  if (res.status === 401 || res.status === 403) {
    throw new ApiError(extractMessage(data, SESSION_MSG), res.status, data);
  }
  if (!res.ok) {
    const fallback =
      res.status === 404 || res.status === 405
        ? "This API endpoint is not available on the server."
        : nonJson
        ? `The server returned an unexpected response (${res.status}).`
        : `Request failed (${res.status}).`;
    throw new ApiError(extractMessage(data, fallback), res.status, data);
  }
  return data;
}

// ---- Assignments (existing endpoint) ----
export async function fetchTechnicianAssignments(technicianId) {
  const data = await request("/api/technician/assignments/", { query: { technician_id: technicianId } });
  return data?.assignments || [];
}

// ---- BMR upload + parse (XHR only to get upload progress) ----
export function uploadBmrPdf({ file, fields = {}, onProgress, onUploadComplete }) {
  return new Promise((resolve, reject) => {
    const headers = authHeaders();
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}${BMR_ENDPOINTS.upload}`);
    xhr.setRequestHeader("Accept", "application/json");
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    // Content-Type intentionally NOT set so the browser adds the multipart boundary.

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
    };
    xhr.upload.onload = () => onUploadComplete?.();

    xhr.onload = () => {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* non-JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        if (data == null) return reject(new ApiError("The server returned an unreadable response.", xhr.status));
        return resolve(data);
      }
      const fallback =
        xhr.status === 404 || xhr.status === 405
          ? "The BMR upload endpoint is not available on the server."
          : xhr.status === 401 || xhr.status === 403
          ? SESSION_MSG
          : `Upload failed (${xhr.status}).`;
      reject(new ApiError(extractMessage(data, fallback), xhr.status, data));
    };
    xhr.onerror = () => reject(new ApiError("Network error while uploading. Please try again."));

    const fd = new FormData();
    fd.append(UPLOAD_FILE_FIELD, file);
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    });
    xhr.send(fd);
  });
}

// ---- BMR upload records (used to restore values the vitals table does not store) ----
export const fetchBmrRecords = (params) => request(BMR_ENDPOINTS.records, { query: params });
export const fetchBmrRecordDetail = (recordId) => request(BMR_ENDPOINTS.recordDetail(recordId));


/* ---- Patient vitals: use patient_unique_id ---- */

export const fetchPatientVitals = (patientUniqueId) => {
  if (
    typeof patientUniqueId !== "string" ||
    !patientUniqueId.trim()
  ) {
    throw new ApiError(
      "The unique patient ID is missing, so vitals cannot be retrieved."
    );
  }

  return request(
    BMR_ENDPOINTS.patientVitals(patientUniqueId.trim())
  );
};

/**
 * Create or update the patient's Vitals row using patient_unique_id.
 *
 * exists === true  -> PATCH; POST only if the record does not exist.
 * exists === false -> POST; PATCH if a record already exists (409).
 * exists === null/undefined -> determine the correct operation by
 * checking the existing record first.
 */
export async function saveVitals({
  patientUniqueId,
  payload,
  exists,
}) {
  if (
    typeof patientUniqueId !== "string" ||
    !patientUniqueId.trim()
  ) {
    throw new ApiError(
      "The unique patient ID is missing, so vitals cannot be saved."
    );
  }

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new ApiError("A valid vitals payload is required.");
  }

  const url = BMR_ENDPOINTS.patientVitals(
    patientUniqueId.trim()
  );

  const send = (method) =>
    request(url, { method, body: payload });

  if (exists === true) {
    try {
      return await send("PATCH");
    } catch (error) {
      if (error.status === 404) {
        return send("POST");
      }
      throw error;
    }
  }

  if (exists === false) {
    try {
      return await send("POST");
    } catch (error) {
      if (error.status === 409) {
        return send("PATCH");
      }
      throw error;
    }
  }

  // If existence is unknown, check the backend before saving.
  const current = await fetchPatientVitals(patientUniqueId);

  const record = current?.record;

  if (record) {
    return send("PATCH");
  }

  return send("POST");
}
